import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { db } from "../../firebase";
import {
  collection, getDocs, doc, getDoc,
  query, where, onSnapshot,
  addDoc, serverTimestamp,
} from "firebase/firestore";
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, Polygon } from "@react-google-maps/api";
import { getMapCenter } from "../../utils/timezoneCoords";
import { startOfDayInTimezone } from "../../utils/dateFormat";
import Modal from "../../components/Modal";
import {
  BarChart2, Map as MapIcon, ClipboardList, Truck, Bell,
  MapPin, Eye, AlertTriangle, Phone, FolderOpen, Tag, Mail,
  Wind, Clock, Globe, ArrowDownCircle, ArrowUpCircle,
  PlayCircle, StopCircle, MessageSquare, Send, ChevronDown, ChevronLeft, ChevronRight, X, Loader2,
  User, Moon, Lock, CheckCircle, EyeOff,
} from "lucide-react";
import { getAuth, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import { useBranding } from "../../context/BrandingContext";
import { GOOGLE_MAPS_KEY, GOOGLE_MAPS_LIBRARIES } from "../../utils/googleMapsConfig";
// eslint-disable-next-line no-unused-vars
const MAP_CENTER = { lat: 25.2048, lng: 55.2708 };

function formatTime(ts) {
  if (!ts) return "—";
  const d    = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Math.floor((new Date() - d) / 60000);
  if (diff < 1)    return "Just now";
  if (diff < 60)   return `${diff}m ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
  return d.toLocaleDateString("en-GB");
}

function isOnline(ts) {
  if (!ts) return false;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return Math.floor((new Date() - d) / 60000) < 10;
}

function getVendorStatus(ts) {
  if (!ts) return "offline";
  const d    = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Math.floor((new Date() - d) / 60000);
  if (diff < 5)  return "online";
  if (diff < 60) return "idle";
  return "offline";
}

const EVENT_LABELS = {
  zone_enter:    { label: "Zone Enter",    color: "bg-blue-100 text-blue-700",     Icon: ArrowDownCircle },
  zone_exit:     { label: "Zone Exit",     color: "bg-orange-100 text-orange-700", Icon: ArrowUpCircle },
  visit:         { label: "Visit",         color: "bg-green-100 text-green-700",   Icon: MapPin },
  checkin:       { label: "Check-in",      color: "bg-green-100 text-green-700",   Icon: MapPin },
  session_start: { label: "Session Start", color: "bg-purple-100 text-purple-700", Icon: PlayCircle },
  session_end:   { label: "Session End",   color: "bg-gray-100 text-gray-700",     Icon: StopCircle },
  alert:         { label: "Alert",         color: "bg-red-100 text-red-700",       Icon: AlertTriangle },
};

const SUP_NAV = [
  { id: "overview", Icon: BarChart2,     labelKey: "nav_overview" },
  { id: "map",      Icon: MapIcon,       labelKey: "nav_live_map" },
  { id: "clients",  Icon: ClipboardList, labelKey: "nav_clients"  },
  { id: "vendors",  Icon: Truck,         labelKey: "nav_vendors"  },
  { id: "events",   Icon: Bell,          labelKey: "nav_events"   },
  { id: "profile",  Icon: User,          labelKey: "nav_profile"  },
];

function SupervisorDashboard() {
  const { branding } = useBranding();
  const { currentUser, companyId, logout, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const [activePage,  setActivePage]  = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDarkMode,  setIsDarkMode]  = useState(() => localStorage.getItem("pinpoint_dark_mode") === "true");
  const [profile, setProfile]         = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [supervisorData, setSupervisorData] = useState(null);
  const [assignedSections, setAssignedSections] = useState([]);
  const [companyName, setCompanyName]   = useState("");
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [vendors, setVendors]     = useState([]);
  const [sections, setSections]   = useState([]);
  const [clients, setClients]     = useState([]);
  const [events, setEvents]       = useState([]);
  const [loadingData, setLoadingData]     = useState(true);
  const [error, setError]                 = useState("");
  const [companyTimezone, setCompanyTimezone] = useState("Asia/Dubai");

  // ── Fix 1: Load supervisor profile — try by UID, fall back to email query ──
  useEffect(() => {
    if (authLoading || !currentUser || !companyId) return;
    async function loadSupervisorProfile() {
      try {
        // Try direct UID lookup first
        let snapData = null;
        let snapId   = null;
        const directSnap = await getDoc(doc(db, "companies", companyId, "users", currentUser.uid));
        if (directSnap.exists()) {
          snapData = directSnap.data();
          snapId   = directSnap.id;
        } else {
          // Fallback: search by email (handles supervisor-xxx doc IDs from Firestore trigger)
          const q      = query(collection(db, "companies", companyId, "users"), where("email", "==", currentUser.email));
          const qSnap  = await getDocs(q);
          if (!qSnap.empty) { snapData = qSnap.docs[0].data(); snapId = qSnap.docs[0].id; }
        }
        if (snapData) {
          setSupervisorData(snapData);
          setProfile({ id: snapId, ...snapData });
          // Handle all section field name variants
          let sections = [];
          if (Array.isArray(snapData.assigned_sections) && snapData.assigned_sections.length > 0) {
            sections = snapData.assigned_sections;
          } else if (snapData.assigned_section) {
            sections = [snapData.assigned_section];
          } else if (Array.isArray(snapData.sections) && snapData.sections.length > 0) {
            sections = snapData.sections;
          }
          setAssignedSections(sections);
          console.log("[SUPERVISOR] Assigned sections:", sections);
        }
      } catch (err) {
        console.error("[SUPERVISOR] Profile load error:", err.message, err.code);
      } finally {
        setLoadingProfile(false);
        setLoadingData(false);
      }
    }
    loadSupervisorProfile();
  }, [authLoading, currentUser, companyId]);

  // Load company name from Firestore to display in header
  useEffect(() => {
    if (!companyId) return;
    async function loadCompanyName() {
      try {
        const [snap, configSnap] = await Promise.all([
          getDoc(doc(db, "companies", companyId)),
          getDoc(doc(db, "companies", companyId, "config", "settings")),
        ]);
        if (snap.exists()) setCompanyName(snap.data().name || "");
        if (configSnap.exists()) setCompanyTimezone(configSnap.data().timezone || "Asia/Dubai");
      } catch (err) {
        console.error("[SUPERVISOR] Company name load error:", err.message);
      }
    }
    loadCompanyName();
  }, [companyId]);

  // ── Fix 2: Load clients filtered by assigned sections ──
  useEffect(() => {
    if (!companyId || assignedSections.length === 0) return;
    async function loadClients() {
      try {
        const clientsQuery = query(
          collection(db, "companies", companyId, "clients"),
          where("section", "in", assignedSections.slice(0, 10))
        );
        const snap = await getDocs(clientsQuery);
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setClients(list);
        console.log("[SUPERVISOR] Clients loaded:", list.length);
      } catch (err) {
        console.error("[SUPERVISOR] Clients error:", err.message, err.code);
      }
    }
    loadClients();
  }, [companyId, assignedSections]);

  // ── Fix 3: Load vendors filtered by assigned sections ──
  useEffect(() => {
    if (!companyId) return;
    async function loadVendors() {
      try {
        const snap       = await getDocs(query(collection(db, "companies", companyId, "users"), where("role", "==", "vendor")));
        const allVendors = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const filtered   = assignedSections.length > 0
          ? allVendors.filter(v => {
              const vSections = Array.isArray(v.assigned_sections) ? v.assigned_sections
                : v.assigned_section ? [v.assigned_section]
                : Array.isArray(v.sections) ? v.sections
                : [];
              return vSections.some(s => assignedSections.includes(s));
            })
          : allVendors;
        setVendors(filtered);
        console.log("[SUPERVISOR] Vendors loaded:", filtered.length, "of", allVendors.length);
      } catch (err) {
        console.error("[SUPERVISOR] Vendors error:", err.message, err.code);
      }
    }
    loadVendors();
  }, [companyId, assignedSections]);

  // ── Fix 4: Load sections filtered by assigned sections ──
  useEffect(() => {
    if (!companyId || assignedSections.length === 0) return;
    async function loadSections() {
      try {
        const snap     = await getDocs(collection(db, "companies", companyId, "sections"));
        const all      = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const filtered = all.filter(s => assignedSections.includes(s.id));
        setSections(filtered);
        console.log("[SUPERVISOR] Sections loaded:", filtered.length);
      } catch (err) {
        console.error("[SUPERVISOR] Sections error:", err.message, err.code);
      }
    }
    loadSections();
  }, [companyId, assignedSections]);

  // ── Fix 5: Load events filtered by vendor IDs (batched for Firestore "in" limit) ──
  useEffect(() => {
    if (!companyId || vendors.length === 0) return;
    async function loadEvents() {
      try {
        const vendorIds = vendors.map(v => v.id);
        const batches   = [];
        for (let i = 0; i < vendorIds.length; i += 10) batches.push(vendorIds.slice(i, i + 10));
        let allEvents = [];
        for (const batch of batches) {
          const snap = await getDocs(query(collection(db, "companies", companyId, "events"), where("vendor_id", "in", batch)));
          allEvents  = [...allEvents, ...snap.docs.map(d => ({ id: d.id, ...d.data() }))];
        }
        allEvents.sort((a, b) => {
          const ta = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(0);
          const tb = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(0);
          return tb - ta;
        });
        setEvents(allEvents);
        console.log("[SUPERVISOR] Events loaded:", allEvents.length);
      } catch (err) {
        console.error("[SUPERVISOR] Events error:", err.message, err.code);
      }
    }
    loadEvents();
  }, [companyId, vendors]);

  // Real-time client listener so green/red pins update when vendors check in
  useEffect(() => {
    if (!companyId || !profile) return;
    const unsub = onSnapshot(
      collection(db, "companies", companyId, "clients"),
      (snap) => {
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setClients(assignedSections.length ? all.filter(c => assignedSections.includes(c.section)) : all);
      },
      (err) => console.error("clients snapshot error:", err.message)
    );
    return () => unsub();
  }, [companyId, profile, assignedSections]);

  // Safety net — if loadingData is still true after 10s, unblock page render
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loadingData) {
        setLoadingData(false);
        setError("Loading took too long. Please refresh the page.");
      }
    }, 10000);
    return () => clearTimeout(timeout);
  }, [loadingData]);

  function toggleDarkMode() {
    const next = !isDarkMode;
    setIsDarkMode(next);
    localStorage.setItem("pinpoint_dark_mode", String(next));
    if (next) document.documentElement.classList.add("dark");
    else      document.documentElement.classList.remove("dark");
  }

  useEffect(() => {
    if (isDarkMode) document.documentElement.classList.add("dark");
    else            document.documentElement.classList.remove("dark");
  }, []); // eslint-disable-line

  function navigate(id) { setActivePage(id); setSidebarOpen(false); }
  const activeItem = SUP_NAV.find(n => n.id === activePage);

  if (loadingProfile) return (
    <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? "bg-gray-900" : "bg-gray-100"}`}>
      <p className="text-gray-500 text-sm">{t("loading")}</p>
    </div>
  );

  return (
    <div className={`min-h-screen flex flex-col ${isDarkMode ? "bg-gray-900" : "bg-gray-100"}`}>

      {/* Navbar */}
      <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="px-4 md:px-6 py-3 flex justify-between items-center w-full">
          <div className="flex items-center gap-2">
            <button className="lg:hidden p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
              onClick={() => setSidebarOpen(o => !o)}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d={sidebarOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
              </svg>
            </button>
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.platform_name} className="w-6 h-6 object-contain rounded" />
            ) : (
              <MapPin className="w-5 h-5 text-purple-600" />
            )}
            <span className="font-bold text-gray-800">{(branding.platform_name || "Pinpoint").toUpperCase()}</span>
            <span className="hidden sm:inline text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
              {t("role_supervisor")}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="lg:hidden text-sm text-gray-500 font-medium">
              {activeItem?.icon} {t(activeItem?.labelKey)}
            </span>
            {profile && (
              <span className="hidden lg:inline text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded-lg font-medium">
                <Eye className="w-3.5 h-3.5 inline mr-1 text-purple-500" />{profile.first_name} {profile.last_name}
              </span>
            )}
            <span className="hidden lg:inline text-xs text-gray-400">{companyName || companyId}</span>
            <span className="hidden md:inline text-sm text-gray-500 truncate max-w-[150px]">{currentUser?.email}</span>
            <button onClick={logout} className="text-xs md:text-sm text-red-500 hover:text-red-700 font-medium">
              {t("logout")}
            </button>
          </div>
        </div>
      </nav>

      {/* ── LAYOUT: full-width flex row — sidebar sticky left, content fills rest. Do NOT add max-w, mx-auto, gap, or ml- here. ── */}
      <div className="flex flex-1 w-full">

        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/30 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)} />
        )}

        <aside className={`
          fixed top-[57px] left-0 z-30 h-[calc(100vh-57px)] w-56 bg-white shadow-xl overflow-y-auto
          transition-transform duration-200
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
          lg:sticky lg:top-[57px] lg:translate-x-0 lg:shadow-none lg:h-[calc(100vh-57px)] lg:w-52 lg:shrink-0 lg:overflow-y-auto lg:border-r lg:border-gray-200 lg:bg-white
        `}>
          <div className="p-3 lg:py-3 lg:px-2">
              <div className="lg:hidden border-b border-gray-100 pb-2 mb-2">
                {profile && <p className="text-xs text-purple-600 font-medium flex items-center gap-1"><Eye className="w-3 h-3" /> {profile.first_name} {profile.last_name}</p>}
                <p className="text-xs text-gray-400 truncate">{currentUser?.email}</p>
              </div>
              <nav className="space-y-0.5">
                {SUP_NAV.map(item => (
                  <SidebarItem key={item.id} Icon={item.Icon} label={t(item.labelKey)}
                    active={activePage === item.id} onClick={() => navigate(item.id)} />
                ))}
              </nav>
              {sections.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-2 mb-2">
                    {t("sup_my_zones")}
                  </p>
                  {sections.map(s => (
                    <div key={s.id} className="flex items-center gap-2 px-2 py-1">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: s.color || "#1A56A0" }} />
                      <span className="text-xs text-gray-600 truncate">{s.name}</span>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </aside>

        <main className="flex-1 min-w-0 p-3 md:p-6 pb-24 lg:pb-6">
          <div className="mb-3">
            <span className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded-full font-medium flex items-center gap-1 w-fit">
              <Eye className="w-3 h-3" /> {t("read_only")}
            </span>
          </div>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm mb-3">{error}</div>
          )}
          {loadingData ? (
            <div className="bg-white rounded-xl shadow-sm p-12 text-center">
              <p className="text-gray-400 text-sm">{t("loading")}</p>
            </div>
          ) : (
            <>
              {activePage === "overview" && <OverviewPage vendors={vendors} clients={clients} events={events} sections={sections} companyId={companyId} companyTimezone={companyTimezone} />}
              {activePage === "map"      && <LiveMapPage  vendors={vendors} sections={sections} clients={clients} companyId={companyId} />}
              {activePage === "clients"  && <ClientsPage  clients={clients} sections={sections} />}
              {activePage === "vendors"  && <VendorsPage  vendors={vendors} sections={sections} companyId={companyId} />}
              {activePage === "events"   && <EventsPage   events={events}   vendors={vendors}   sections={sections} />}
              {activePage === "profile"  && <SupProfilePage currentUser={currentUser} companyId={companyId} profile={profile} companyName={companyName} isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />}
            </>
          )}
        </main>
      </div>

      {/* Mobile Bottom Tab Bar */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40">
        <div className="flex">
          {SUP_NAV.map(item => {
            const isActive = activePage === item.id;
            return (
              <button key={item.id} onClick={() => navigate(item.id)}
                className={`flex-1 flex flex-col items-center justify-center py-2 gap-0.5 transition-colors ${
                  isActive ? "text-purple-700" : "text-gray-400 active:bg-gray-50"
                }`}>
                {item.Icon && (() => { const Icon = item.Icon; return <Icon className="w-5 h-5" />; })()}
                <span className="text-[9px] font-medium leading-none">{t(item.labelKey)}</span>
                {isActive && <span className="w-1 h-1 rounded-full bg-purple-600 mt-0.5" />}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

function OverviewPage({ vendors, clients, events, sections, companyTimezone = "Asia/Dubai" }) {
  const { t } = useLanguage();
  // eslint-disable-next-line no-unused-vars
  const activeVendors = vendors.filter(v => v.active !== false).length;
  const alerts        = events.filter(e => e.type === "zone_enter" || e.type === "zone_exit").length;
  const recentEvents  = events.slice(0, 8);

  // Unique clients visited today — use company timezone midnight
  const startOfToday = startOfDayInTimezone(companyTimezone);
  const todayCheckins = events.filter(e => {
    if (e.type !== "checkin") return false;
    const ts = e.timestamp?.toDate ? e.timestamp.toDate() : new Date(e.timestamp);
    return ts >= startOfToday;
  });
  const visits = new Set(todayCheckins.map(e => e.client_id).filter(Boolean)).size;

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard icon={Truck}         label={t("sup_dash_my_vendors")}    value={vendors.length}  color="purple" />
        <StatCard icon={ClipboardList} label={t("sup_dash_my_clients")}    value={clients.length}  color="blue" />
        <StatCard icon={MapPin}        label={t("sup_dash_clients_today")} value={visits}          color="green" />
        <StatCard icon={AlertTriangle} label={t("sup_dash_zone_alerts")}   value={alerts}          color="red" />
      </div>

      {/* Zones summary */}
      {sections.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">{t("sup_dash_assigned_zones")}</h3>
          <div className="grid grid-cols-3 gap-3">
            {sections.map(section => {
              const zoneClients = clients.filter(c => c.section === section.id).length;
              return (
                <div key={section.id} className="rounded-xl p-4 border border-gray-100"
                  style={{ borderLeftColor: section.color || "#1A56A0", borderLeftWidth: 4 }}>
                  <p className="font-medium text-gray-800 text-sm">{section.name}</p>
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1"><ClipboardList className="w-3 h-3" /> {zoneClients} {t("sup_dash_client_page")}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent events */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">{t("sup_dash_recent_activity")}</h3>
        {recentEvents.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-4">{t("sup_dash_no_events")}</p>
        ) : (
          <div className="space-y-2">
            {recentEvents.map(ev => {
              const meta = EVENT_LABELS[ev.type] || { label: ev.type, color: "bg-gray-100 text-gray-600", Icon: MapPin };
              return (
                <div key={ev.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${meta.color}`}>
                      {(() => { const I = meta.Icon; return I ? <I className="w-3 h-3 inline mr-0.5" /> : null; })()}{meta.label}
                    </span>
                    <div className="min-w-0">
                      {ev.vendor_name && (
                        <p className="text-xs font-medium text-gray-700 truncate">{ev.vendor_name}</p>
                      )}
                      {ev.client_name && (
                        <p className="text-xs text-gray-400 truncate">→ {ev.client_name}</p>
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{formatTime(ev.timestamp)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Live Map Page
// ─────────────────────────────────────────────────────────────────────────────
function LiveMapPage({ vendors, sections, clients, companyId }) {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const [liveLocations, setLiveLocations]   = useState({});
  const [selectedMarker,  setSelectedMarker]  = useState(null);
  const [selectedSection, setSelectedSection] = useState(null); // { section, pos, total, green }
  const [showClients, setShowClients]         = useState(true);
  const [mapType, setMapType]               = useState("roadmap");
  const [visitResetDays,  setVisitResetDays]  = useState(1);
  const [companyTimezone, setCompanyTimezone] = useState("Asia/Dubai");
  const [mapCenter, setMapCenter]             = useState({ lat: 25.2048, lng: 55.2708 });
  const [mapZoom, setMapZoom]               = useState(11);
  const centeredOnVendorRef                 = useRef(false);

  // Load visit_reset_days and timezone-based map center
  useEffect(() => {
    async function loadCompanySettings() {
      try {
        const [companySnap, configSnap] = await Promise.all([
          getDoc(doc(db, "companies", companyId)),
          getDoc(doc(db, "companies", companyId, "config", "settings")),
        ]);
        if (companySnap.exists()) setVisitResetDays(companySnap.data().visit_reset_days ?? 1);
        const timezone = configSnap.exists() ? (configSnap.data().timezone || "Asia/Dubai") : "Asia/Dubai";
        setCompanyTimezone(timezone);
        const center = getMapCenter(timezone);
        setMapCenter({ lat: center.lat, lng: center.lng });
        setMapZoom(center.zoom);
      } catch (err) { console.error("loadCompanySettings:", err.message); }
    }
    loadCompanySettings();
  }, [companyId]);

  // If any vendor has a live location, center on them (only once)
  useEffect(() => {
    if (centeredOnVendorRef.current) return;
    const firstLoc = Object.values(liveLocations).find(loc => loc.lat && loc.lng);
    if (firstLoc) {
      setMapCenter({ lat: firstLoc.lat, lng: firstLoc.lng });
      setMapZoom(13);
      centeredOnVendorRef.current = true;
    }
  }, [liveLocations]);

  // ── Send Note state ───────────────────────────────────────────────
  const [noteModal,   setNoteModal]   = useState(false);
  const [noteClient,  setNoteClient]  = useState(null);
  const [noteSaving,  setNoteSaving]  = useState(false);
  const [noteError,   setNoteError]   = useState("");
  const [noteSuccess, setNoteSuccess] = useState("");
  const emptyNote = { message: "", priority: "normal" };
  const [noteForm, setNoteForm] = useState(emptyNote);

  function openNoteModal(client) {
    setSelectedMarker(null);
    setNoteClient(client);
    setNoteForm(emptyNote);
    setNoteError("");
    setNoteSuccess("");
    setNoteModal(true);
  }

  async function sendNote() {
    if (!noteForm.message.trim()) return setNoteError("Please enter a message.");
    setNoteSaving(true);
    setNoteError("");
    try {
      const clientName = noteClient.name ||
        `${noteClient.first_name || ""} ${noteClient.last_name || ""}`.trim();
      await addDoc(collection(db, "companies", companyId, "notes"), {
        client_id:       noteClient.id,
        client_name:     clientName,
        client_phone:    noteClient.phone    || "",
        client_location: noteClient.location || null,
        message:         noteForm.message.trim(),
        priority:        noteForm.priority,
        sent_by:         currentUser.uid,
        sent_by_name:    currentUser.displayName || currentUser.email,
        sent_by_role:    "supervisor",
        created_at:      serverTimestamp(),
        read:            false,
      });
      setNoteSuccess("Note sent to admin.");
      setTimeout(() => {
        setNoteModal(false);
        setNoteClient(null);
        setNoteSuccess("");
      }, 1500);
    } catch (err) {
      setNoteError("Error: " + err.message);
    }
    setNoteSaving(false);
  }

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // Live location listener — only for assigned vendors
  useEffect(() => {
    if (!companyId || vendors.length === 0) return;
    const vendorIds = new Set(vendors.map(v => v.id));

    const unsub = onSnapshot(
      collection(db, "companies", companyId, "events"),
      (snap) => {
        const locations = {};
        snap.docs.forEach(d => {
          const data = d.data();
          if (data.vendor_id && vendorIds.has(data.vendor_id) && data.lat && data.lng) {
            if (!locations[data.vendor_id] ||
              (data.timestamp?.toMillis?.() > locations[data.vendor_id].timestamp?.toMillis?.())) {
              locations[data.vendor_id] = {
                lat: data.lat, lng: data.lng,
                timestamp: data.timestamp,
                speed: data.speed || 0,
                type:  data.type  || "",
              };
            }
          }
        });
        setLiveLocations(locations);
      }
    );
    return () => unsub();
  }, [companyId, vendors]);

  const onlineCount = vendors.filter(v => {
    const loc = liveLocations[v.id];
    return loc && getVendorStatus(loc.timestamp) !== "offline";
  }).length;

  function isClientGreen(client) {
    const s = client.last_status;
    if (s !== "arrived" && s !== "done") return false;
    const visitedAt = client.last_visited_at;
    if (!visitedAt) return false;
    const d = visitedAt.toDate ? visitedAt.toDate() : new Date(visitedAt);
    return (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24) < visitResetDays;
  }

  function handleZoneClick(section, e) {
    const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    const gmPolygon = new window.google.maps.Polygon({ paths: section.polygon });
    let total = 0, green = 0;
    for (const client of clients) {
      const lat = client.location?.lat;
      const lng = client.location?.lng;
      if (lat == null || lng == null) continue;
      const inside = window.google.maps.geometry.poly.containsLocation(
        new window.google.maps.LatLng(lat, lng), gmPolygon
      );
      if (inside) { total++; if (isClientGreen(client)) green++; }
    }
    setSelectedSection({ section, pos, total, green });
    setSelectedMarker(null);
  }

  if (!isLoaded) return (
    <div className="bg-white rounded-xl shadow-sm p-12 text-center">
      <p className="text-gray-400 text-sm">{t("cmap_loading_map")}</p>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-5 flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">{t("sup_dash_live_map")}</h2>
          <p className="text-gray-500 text-sm mt-0.5">
            {vendors.length} {t("sup_dash_vendor_page")} •{" "}
            <span className="text-green-600 font-medium flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> {onlineCount} {t("sup_dash_online")}</span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input type="checkbox" checked={showClients} onChange={e => setShowClients(e.target.checked)}
              className="accent-green-600" />
            {t("sup_dash_show_clients")}
          </label>
          <button onClick={() => setMapType(mt => mt === "roadmap" ? "satellite" : "roadmap")}
            className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg font-medium">
            {mapType === "roadmap" ? <span className="flex items-center gap-1.5"><Globe className="w-4 h-4" /> {t("sup_dash_satellite")}</span> : <span className="flex items-center gap-1.5"><MapIcon className="w-4 h-4" /> {t("sup_dash_map")}</span>}
          </button>
        </div>
      </div>

      {/* Map */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "520px" }}
          center={mapCenter} zoom={mapZoom} mapTypeId={mapType}
          options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: true }}>

          {/* Zone polygons */}
          {sections.map(section => (
            section.polygon && section.polygon.length > 2 && (
              <Polygon key={section.id} paths={section.polygon}
                options={{ fillColor: section.color || "#1A56A0", fillOpacity: 0.15, strokeColor: section.color || "#1A56A0", strokeWeight: 2, clickable: true }}
                onClick={e => handleZoneClick(section, e)} />
            )
          ))}


          {/* Vendor markers */}
          {vendors.map(vendor => {
            const loc = liveLocations[vendor.id];
            if (!loc) return null;
            const status  = getVendorStatus(loc.timestamp);
            const opacity = status === "offline" ? 0.4 : status === "idle" ? 0.7 : 1;
            return (
              <Marker key={vendor.id} position={{ lat: loc.lat, lng: loc.lng }}
                opacity={opacity}
                onClick={() => setSelectedMarker({ type: "vendor", id: vendor.id })}
                icon={{
                  url: (loc.speed > 0) ? "/icons/car-green.png" : "/icons/car.png",
                  scaledSize: new window.google.maps.Size(40, 40),
                  anchor: new window.google.maps.Point(20, 20),
                }} />
            );
          })}

          {/* Client markers */}
          {showClients && clients.filter(c => c.location?.lat && c.location?.lng).map(client => (
            <Marker key={client.id}
              position={{ lat: client.location.lat, lng: client.location.lng }}
              onClick={() => setSelectedMarker({ type: "client", id: client.id })}
              icon={{
                url: (() => {
                  const s = client.last_status;
                  if (s === "arrived" || s === "done") {
                    const visitedAt = client.last_visited_at;
                    if (visitedAt) {
                      const d = visitedAt.toDate ? visitedAt.toDate() : new Date(visitedAt);
                      const daysSinceVisit = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
                      if (daysSinceVisit < visitResetDays) {
                        return "/icons/green-pin.png";
                      }
                    }
                  }
                  return "/icons/red-pin.png";
                })(),
                scaledSize: new window.google.maps.Size(18, 18),
                anchor: new window.google.maps.Point(9, 18),
              }} />
          ))}

          {/* InfoWindows */}
          {selectedMarker?.type === "vendor" && (() => {
            const vendor = vendors.find(v => v.id === selectedMarker.id);
            const loc    = liveLocations[selectedMarker.id];
            if (!vendor || !loc) return null;
            return (
              <InfoWindow position={{ lat: loc.lat, lng: loc.lng }} onCloseClick={() => setSelectedMarker(null)}>
                <div className="p-1 min-w-36">
                  <p className="font-semibold text-gray-800 text-sm flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> {vendor.first_name} {vendor.last_name}</p>
                  {vendor.plate && <p className="text-xs text-gray-500 mt-0.5">Plate: {vendor.plate}</p>}
                  <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1"><Clock className="w-3 h-3" /> {formatTime(loc.timestamp)}</p>
                  {loc.speed > 0 && <p className="text-xs text-gray-500 flex items-center gap-1"><Wind className="w-3 h-3" /> {loc.speed} km/h</p>}
                  {(() => {
                    const st = getVendorStatus(loc.timestamp);
                    const dot  = st === "online" ? "bg-green-500" : st === "idle" ? "bg-yellow-500" : "bg-gray-400";
                    const text = st === "online" ? "text-green-600" : st === "idle" ? "text-yellow-600" : "text-gray-400";
                    return (
                      <p className={`text-xs font-medium mt-1 flex items-center gap-1 ${text}`}>
                        <span className={`w-2 h-2 rounded-full inline-block ${dot}`} />
                        {st === "online" ? t("cmap_online") : st === "idle" ? t("cmap_idle") : t("cmap_offline")}
                      </p>
                    );
                  })()}
                </div>
              </InfoWindow>
            );
          })()}

          {selectedMarker?.type === "client" && (() => {
            const client = clients.find(c => c.id === selectedMarker.id);
            if (!client?.location) return null;
            return (
              <InfoWindow position={{ lat: client.location.lat, lng: client.location.lng }} onCloseClick={() => setSelectedMarker(null)}>
                <div className="p-1 min-w-36">
                  <p className="font-semibold text-gray-800 text-sm flex items-center gap-1"><ClipboardList className="w-3.5 h-3.5" /> {client.first_name} {client.last_name || client.name}</p>
                  {client.phone   && <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1"><Phone className="w-3 h-3" /> {client.phone}</p>}
                  {client.address && <p className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {client.address}</p>}
                  <button
                    onClick={() => openNoteModal(client)}
                    style={{ marginTop: 8, width: "100%", background: "#7c3aed", color: "white", border: "none", borderRadius: 8, padding: "6px 0", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                    <MessageSquare style={{ width: 12, height: 12 }} />
                    {t("sup_dash_send_note")}
                  </button>
                </div>
              </InfoWindow>
            );
          })()}
        </GoogleMap>

        {/* Zone stats modal */}
        <Modal isOpen={!!selectedSection} onClose={() => setSelectedSection(null)}
          title={selectedSection?.section.name || "Zone"} size="sm">
          {selectedSection && (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: (selectedSection.section.color || "#1A56A0") + "22",
                           border: `2px solid ${selectedSection.section.color || "#1A56A0"}` }}>
                  <MapIcon className="w-5 h-5" style={{ color: selectedSection.section.color || "#1A56A0" }} />
                </div>
                <div className="flex-1">
                  <div className="grid grid-cols-2 gap-3 mt-1">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-gray-800">{selectedSection.total}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{t("sup_dash_total_clients")}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-600">{selectedSection.green}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{t("cmap_visited")}</p>
                    </div>
                  </div>
                  {selectedSection.total > 0 && (
                    <div className="mt-3">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>{t("cmap_progress")}</span>
                        <span className="font-medium">{Math.round((selectedSection.green / selectedSection.total) * 100)}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full transition-all"
                          style={{ width: `${Math.round((selectedSection.green / selectedSection.total) * 100)}%` }} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex pt-2 border-t">
                <button onClick={() => setSelectedSection(null)}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-5 py-2 rounded-lg">
                  {t("cmap_close")}
                </button>
              </div>
            </div>
          )}
        </Modal>

        {/* Legend */}
        <div className="px-5 py-3 bg-gray-50 flex items-center gap-6 text-xs text-gray-500">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-green-500 inline-block"></span>{t("sup_dash_online_vendor")}</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-gray-400 inline-block"></span>{t("sup_dash_offline_vendor")}</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-blue-400 inline-block"></span>{t("sup_dash_client_label")}</span>
          <span className="flex items-center gap-1.5">{t("sup_dash_zones_legend")}</span>
        </div>
      </div>

      {/* Vendor status list */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">{t("sup_dash_vendor_page")}</h3>
        <div className="space-y-2">
          {vendors.map(vendor => {
            const loc    = liveLocations[vendor.id];
            const status = loc ? getVendorStatus(loc.timestamp) : "offline";
            const dot    = status === "online" ? "bg-green-500" : status === "idle" ? "bg-yellow-500" : "bg-gray-300";
            const text   = status === "online" ? "text-green-600" : status === "idle" ? "text-yellow-600" : "text-gray-400";
            const label  = status === "online" ? t("cmap_online") : status === "idle" ? t("cmap_idle") : t("cmap_offline");
            return (
              <div key={vendor.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <div className="flex items-center gap-3">
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dot}`} />
                  <div>
                    <p className="text-sm font-medium text-gray-800">{vendor.first_name} {vendor.last_name}</p>
                    {vendor.plate && <p className="text-xs text-gray-400 flex items-center gap-1"><Truck className="w-3 h-3" /> {vendor.plate}</p>}
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-xs font-medium flex items-center gap-1 justify-end ${text}`}>
                    <span className={`w-2 h-2 rounded-full inline-block ${dot}`} />
                    {label}
                  </p>
                  {loc && <p className="text-xs text-gray-400">{formatTime(loc.timestamp)}</p>}
                </div>
              </div>
            );
          })}
          {vendors.length === 0 && (
            <p className="text-gray-400 text-sm text-center py-4">{t("sup_dash_no_vendors_yet")}</p>
          )}
        </div>
      </div>

      {/* ── Send Note Modal ───────────────────────────────────────── */}
      {noteModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setNoteModal(false)} />
          <div className="relative bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-sm shadow-2xl">
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-purple-600" />
                <h2 className="text-base font-bold text-gray-800">{t("sup_dash_send_note_title")}</h2>
              </div>
              <button onClick={() => setNoteModal(false)}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center active:bg-gray-200">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="px-5 pb-6 space-y-4">
              <div className="bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-600 font-medium">
                {noteClient?.name || `${noteClient?.first_name || ""} ${noteClient?.last_name || ""}`.trim()}
              </div>
              {noteError && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 flex items-center gap-2 text-sm">
                  <AlertTriangle className="w-4 h-4 shrink-0" />{noteError}
                </div>
              )}
              {noteSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl p-3 text-sm">
                  {noteSuccess}
                </div>
              )}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  {t("sup_dash_note_message")} <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={noteForm.message}
                  onChange={e => setNoteForm(p => ({ ...p, message: e.target.value }))}
                  rows={4}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
                  placeholder={t("sup_dash_note_describe_ph")} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t("sup_dash_note_priority")}</label>
                <div className="relative">
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <select
                    value={noteForm.priority}
                    onChange={e => setNoteForm(p => ({ ...p, priority: e.target.value }))}
                    className="w-full pr-10 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 appearance-none bg-white">
                    <option value="normal">{t("sup_dash_note_normal")}</option>
                    <option value="urgent">{t("sup_dash_note_urgent")}</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <button onClick={() => setNoteModal(false)}
                  className="flex-1 border border-gray-200 text-gray-600 font-medium py-3 rounded-2xl hover:bg-gray-50 transition text-sm">
                  {t("sup_dash_cancel")}
                </button>
                <button onClick={sendNote} disabled={noteSaving}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white font-bold py-3 rounded-2xl flex items-center justify-center gap-2 transition text-sm">
                  {noteSaving
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Send className="w-4 h-4" />
                  }
                  {noteSaving ? t("sup_dash_note_sending") : t("sup_dash_note_send")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Clients Page (read-only) — paginated 50 per page
// ─────────────────────────────────────────────────────────────────────────────
const CLIENTS_PAGE_SIZE = 50;

function ClientsPage({ clients, sections }) {
  const { t } = useLanguage();
  const [search, setSearch]               = useState("");
  const [filterSection, setFilterSection] = useState("all");
  const [currentPage, setCurrentPage]     = useState(1);

  // Reset to page 1 whenever filters change
  useEffect(() => { setCurrentPage(1); }, [search, filterSection]);

  function getSectionName(id) {
    return sections.find(s => s.id === id)?.name || id;
  }

  const filtered = clients.filter(c => {
    const matchSearch  = !search || `${c.first_name || ""} ${c.last_name || ""} ${c.name || ""} ${c.phone || ""} ${c.address || ""}`
      .toLowerCase().includes(search.toLowerCase());
    const matchSection = filterSection === "all" || c.section === filterSection;
    return matchSearch && matchSection;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / CLIENTS_PAGE_SIZE));
  const paginated  = filtered.slice((currentPage - 1) * CLIENTS_PAGE_SIZE, currentPage * CLIENTS_PAGE_SIZE);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm p-6 flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">{t("sup_dash_client_page")}</h2>
          <p className="text-gray-500 text-sm mt-1">{clients.length} {t("sup_dash_clients_in_zones")}</p>
        </div>
        <span className="text-xs bg-gray-100 text-gray-500 px-3 py-1.5 rounded-lg flex items-center gap-1">
          <Eye className="w-3 h-3" /> {t("sup_dash_read_only")}
        </span>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex gap-3 flex-wrap">
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder={t("sup_dash_search_clients")}
          className="flex-1 min-w-48 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
        <select value={filterSection} onChange={e => setFilterSection(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400">
          <option value="all">{t("sup_dash_all_zones")}</option>
          {sections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {paginated.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <ClipboardList className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">{t("sup_dash_no_clients_found")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {paginated.map(client => (
            <div key={client.id} className="bg-white rounded-xl shadow-sm p-5 flex items-center gap-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold flex-shrink-0 ${
                client.active !== false ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"
              }`}>
                {(client.first_name || client.name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-800">
                  {client.first_name} {client.last_name}{!client.first_name && client.name}
                </p>
                <div className="flex flex-wrap gap-3 mt-0.5">
                  {client.phone   && <p className="text-gray-400 text-xs flex items-center gap-1"><Phone className="w-3 h-3" /> {client.phone}</p>}
                  {client.address && <p className="text-gray-400 text-xs flex items-center gap-1"><MapPin className="w-3 h-3" /> {client.address}</p>}
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {client.section && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      <FolderOpen className="w-3 h-3 inline mr-0.5" />{getSectionName(client.section)}
                    </span>
                  )}
                  {client.type && (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                      <Tag className="w-3 h-3 inline mr-0.5" />{client.type}
                    </span>
                  )}
                  {client.active === false && (
                    <span className="text-xs bg-red-50 text-red-500 px-2 py-0.5 rounded-full">Inactive</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination controls — only shown when there is more than one page */}
      {filtered.length > CLIENTS_PAGE_SIZE && (
        <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {((currentPage - 1) * CLIENTS_PAGE_SIZE) + 1}–{Math.min(currentPage * CLIENTS_PAGE_SIZE, filtered.length)} of {filtered.length} clients
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-40 flex items-center gap-1">
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>
            <span className="text-sm font-medium text-gray-700 px-2">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-40 flex items-center gap-1">
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Vendors Page (read-only)
// ─────────────────────────────────────────────────────────────────────────────
function VendorsPage({ vendors, sections, companyId }) {
  const { t } = useLanguage();
  const [liveLocations, setLiveLocations] = useState({});

  useEffect(() => {
    if (!companyId || vendors.length === 0) return;
    const vendorIds = new Set(vendors.map(v => v.id));
    const unsub = onSnapshot(collection(db, "companies", companyId, "events"), (snap) => {
      const locations = {};
      snap.docs.forEach(d => {
        const data = d.data();
        if (data.vendor_id && vendorIds.has(data.vendor_id) && data.lat && data.lng) {
          if (!locations[data.vendor_id] ||
            (data.timestamp?.toMillis?.() > locations[data.vendor_id].timestamp?.toMillis?.())) {
            locations[data.vendor_id] = { lat: data.lat, lng: data.lng, timestamp: data.timestamp, speed: data.speed || 0 };
          }
        }
      });
      setLiveLocations(locations);
    });
    return () => unsub();
  }, [companyId, vendors]);

  function getSectionNames(ids) {
    if (!ids?.length) return [];
    return ids.map(id => sections.find(s => s.id === id)?.name || id);
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm p-6 flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">{t("sup_dash_vendor_page")}</h2>
          <p className="text-gray-500 text-sm mt-1">{vendors.length} {t("sup_dash_vendors_assigned")}</p>
        </div>
        <span className="text-xs bg-gray-100 text-gray-500 px-3 py-1.5 rounded-lg flex items-center gap-1"><Eye className="w-3 h-3" /> {t("sup_dash_read_only")}</span>
      </div>

      {vendors.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <Truck className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">{t("sup_dash_no_vendors_yet")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {vendors.map(vendor => {
            const loc       = liveLocations[vendor.id];
            const online    = loc && isOnline(loc.timestamp);
            const zoneNames = getSectionNames(vendor.sections);
            return (
              <div key={vendor.id} className="bg-white rounded-xl shadow-sm p-5">
                <div className="flex items-center gap-4">
                  <div className="relative flex-shrink-0">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center font-bold text-lg ${
                      vendor.active !== false ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"
                    }`}>
                      {(vendor.first_name || "?").charAt(0).toUpperCase()}
                    </div>
                    {loc && (
                      <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
                        online ? "bg-green-400" : "bg-gray-400"
                      }`} />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-gray-800">{vendor.first_name} {vendor.last_name}</p>
                      {loc && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          online ? "bg-green-50 text-green-700" : "bg-gray-50 text-gray-500"
                        }`}>
                          <span className="flex items-center gap-1"><span className={`w-2 h-2 rounded-full inline-block ${online ? "bg-green-500" : "bg-gray-400"}`} />{online ? t("cmap_online") : formatTime(loc.timestamp)}</span>
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-3 mt-1">
                      {vendor.email && <p className="text-gray-400 text-xs flex items-center gap-1"><Mail className="w-3 h-3" /> {vendor.email}</p>}
                      {vendor.phone && <p className="text-gray-400 text-xs flex items-center gap-1"><Phone className="w-3 h-3" /> {vendor.phone}</p>}
                      {vendor.plate && (
                        <p className="text-gray-500 text-xs font-mono bg-gray-100 px-2 py-0.5 rounded">
                          <Truck className="w-3 h-3 inline mr-0.5" />{vendor.plate}
                        </p>
                      )}
                    </div>
                    {zoneNames.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {zoneNames.map((name, i) => (
                          <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                            <FolderOpen className="w-3 h-3 inline mr-0.5" />{name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    {loc?.speed > 0 && (
                      <p className="text-xs text-gray-500 flex items-center gap-1"><Wind className="w-3 h-3" /> {loc.speed} km/h</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Events Page (read-only delivery history)
// ─────────────────────────────────────────────────────────────────────────────
function EventsPage({ events, vendors, sections }) {
  const { t } = useLanguage();
  const [filterType, setFilterType]   = useState("all");
  const [filterVendor, setFilterVendor] = useState("all");
  const [search, setSearch]           = useState("");

  function getVendorName(id) {
    const v = vendors.find(v => v.id === id);
    return v ? `${v.first_name} ${v.last_name}` : id;
  }

  const typeCounts = events.reduce((acc, e) => {
    acc[e.type] = (acc[e.type] || 0) + 1;
    return acc;
  }, {});

  const filtered = events.filter(e => {
    const matchType   = filterType   === "all" || e.type === filterType;
    const matchVendor = filterVendor === "all" || e.vendor_id === filterVendor;
    const matchSearch = !search || `${e.vendor_name || ""} ${e.client_name || ""}`.toLowerCase().includes(search.toLowerCase());
    return matchType && matchVendor && matchSearch;
  });

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm p-6 flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">{t("sup_dash_events_history")}</h2>
          <p className="text-gray-500 text-sm mt-1">{events.length} {t("sup_dash_events_zones")}</p>
        </div>
        <span className="text-xs bg-gray-100 text-gray-500 px-3 py-1.5 rounded-lg flex items-center gap-1"><Eye className="w-3 h-3" /> {t("sup_dash_read_only")}</span>
      </div>

      {/* Type filter tabs */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap gap-2">
        {[
          { value: "all",           label: t("sup_dash_all"),           count: events.length },
          { value: "visit",         label: t("sup_dash_visits"),        count: typeCounts.visit        || 0 },
          { value: "zone_enter",    label: t("sup_dash_zone_enter"),    count: typeCounts.zone_enter   || 0 },
          { value: "zone_exit",     label: t("sup_dash_zone_exit"),     count: typeCounts.zone_exit    || 0 },
          { value: "session_start", label: t("sup_dash_session_start"), count: typeCounts.session_start || 0 },
          { value: "session_end",   label: t("sup_dash_session_end"),   count: typeCounts.session_end   || 0 },
        ].map(tab => (
          <button key={tab.value} onClick={() => setFilterType(tab.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition ${
              filterType === tab.value
                ? "bg-purple-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}>
            {tab.label}
            <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
              filterType === tab.value ? "bg-purple-500 text-white" : "bg-gray-200 text-gray-600"
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Search + vendor filter */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex gap-3 flex-wrap">
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder={t("sup_dash_search_events")}
          className="flex-1 min-w-48 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
        <select value={filterVendor} onChange={e => setFilterVendor(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400">
          <option value="all">{t("sup_dash_all_vendors")}</option>
          {vendors.map(v => <option key={v.id} value={v.id}>{v.first_name} {v.last_name}</option>)}
        </select>
      </div>

      {/* Events list */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <Bell className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">{t("sup_dash_no_events_filter")}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="divide-y divide-gray-50">
            {filtered.map(ev => {
              const meta = EVENT_LABELS[ev.type] || { label: ev.type, color: "bg-gray-100 text-gray-600", Icon: MapPin };
              return (
                <div key={ev.id} className="px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition">
                  <div className="flex items-center gap-4">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0 ${meta.color}`}>
                      {(() => { const I = meta.Icon; return I ? <I className="w-3 h-3 inline mr-0.5" /> : null; })()}{meta.label}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        {ev.vendor_name || getVendorName(ev.vendor_id) || "Unknown"}
                      </p>
                      {ev.client_name && (
                        <p className="text-xs text-gray-500 mt-0.5">→ {ev.client_name}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-gray-400">{formatTime(ev.timestamp)}</p>
                    {ev.section_id && (
                      <p className="text-xs text-blue-500 mt-0.5">
                        <FolderOpen className="w-3 h-3 inline mr-0.5" />{sections.find(s => s.id === ev.section_id)?.name || ev.section_id}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared UI components
// ─────────────────────────────────────────────────────────────────────────────
function SidebarItem({ Icon, label, active, onClick }) {
  return (
    <button onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition ${
        active ? "bg-purple-50 text-purple-700" : "text-gray-600 hover:bg-gray-50"
      }`}>
      {Icon && <Icon className="w-4 h-4" />}
      <span>{label}</span>
    </button>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    purple: "bg-purple-50 text-purple-700",
    blue:   "bg-blue-50 text-blue-700",
    green:  "bg-green-50 text-green-700",
    red:    "bg-red-50 text-red-700",
  };
  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
        {Icon && <Icon className="w-5 h-5" />}
      </div>
      <p className="text-2xl font-bold text-gray-800 mt-3">{value}</p>
      <p className="text-gray-500 text-sm mt-1">{label}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Supervisor Profile Page
// ─────────────────────────────────────────────────────────────────────────────
function SupProfilePage({ currentUser, companyId, profile, companyName, isDarkMode, toggleDarkMode }) {
  const { t } = useLanguage();
  const [currentPw,   setCurrentPw]   = useState("");
  const [newPw,       setNewPw]       = useState("");
  const [confirmPw,   setConfirmPw]   = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew,     setShowNew]     = useState(false);
  const [pwSaving,    setPwSaving]    = useState(false);
  const [pwError,     setPwError]     = useState("");
  const [pwSuccess,   setPwSuccess]   = useState("");

  async function handleChangePassword() {
    setPwError(""); setPwSuccess("");
    if (!currentPw || !newPw || !confirmPw) return setPwError("All fields are required.");
    if (newPw !== confirmPw) return setPwError("New passwords do not match.");
    if (newPw.length < 6) return setPwError("Password must be at least 6 characters.");
    setPwSaving(true);
    try {
      const auth       = getAuth();
      const credential = EmailAuthProvider.credential(currentUser.email, currentPw);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, newPw);
      setPwSuccess("Password updated successfully.");
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      setPwError("Error: " + (err.message || err.code));
    }
    setPwSaving(false);
  }

  const card  = isDarkMode ? "bg-gray-800" : "bg-white";
  const hdr   = isDarkMode ? "text-gray-200" : "text-gray-700";
  const txt   = isDarkMode ? "text-gray-300" : "text-gray-600";

  return (
    <div className="max-w-lg space-y-5">
      {/* Profile card */}
      <div className={`${card} rounded-xl shadow-sm p-5`}>
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xl font-bold shrink-0">
            {(profile?.first_name?.[0] || currentUser.email?.[0] || "S").toUpperCase()}
          </div>
          <div>
            <p className={`font-bold text-lg ${hdr}`}>
              {profile ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim() : currentUser.email}
            </p>
            <p className={`text-sm ${txt}`}>{currentUser.email}</p>
            {companyName && <p className="text-xs text-purple-500 mt-0.5">{companyName}</p>}
          </div>
        </div>
        {profile?.phone && (
          <div className={`flex items-center gap-2 text-sm ${txt}`}>
            <Phone className="w-4 h-4 text-gray-400 shrink-0" /> {profile.phone}
          </div>
        )}
      </div>

      {/* Appearance */}
      <div className={`${card} rounded-xl shadow-sm p-5`}>
        <h3 className={`text-sm font-bold mb-4 flex items-center gap-2 ${hdr}`}>
          <Moon className="w-4 h-4" /> {t("sup_dash_appearance")}
        </h3>
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>{t("sup_dash_dark_mode")}</p>
            <p className="text-xs text-gray-400 mt-0.5">{t("sup_dash_dark_hint")}</p>
          </div>
          <button
            onClick={toggleDarkMode}
            className={`relative inline-flex items-center rounded-full w-11 h-6 transition-colors focus:outline-none ${isDarkMode ? "bg-blue-600" : "bg-gray-300"}`}>
            <span className={`inline-block w-5 h-5 bg-white rounded-full shadow transform transition-transform ${isDarkMode ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </div>
      </div>

      {/* Change password */}
      <div className={`${card} rounded-xl shadow-sm p-5`}>
        <h3 className={`text-sm font-bold mb-4 flex items-center gap-2 ${hdr}`}>
          <Lock className="w-4 h-4" /> {t("sup_dash_change_pw")}
        </h3>
        {pwError && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 shrink-0" />{pwError}
          </div>
        )}
        {pwSuccess && (
          <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl p-3 text-sm mb-3">{pwSuccess}</div>
        )}
        <div className="space-y-3">
          {[
            { label: t("sup_dash_current_pw"), val: currentPw, set: setCurrentPw, show: showCurrent, toggle: setShowCurrent },
            { label: t("sup_dash_new_pw"),     val: newPw,     set: setNewPw,     show: showNew,     toggle: setShowNew     },
            { label: t("sup_dash_confirm_pw"), val: confirmPw, set: setConfirmPw, show: showNew,     toggle: setShowNew     },
          ].map((f, i) => (
            <div key={i}>
              <label className={`block text-sm font-medium mb-1 ${hdr}`}>{f.label}</label>
              <div className="relative">
                <input
                  type={f.show ? "text" : "password"}
                  value={f.val}
                  onChange={e => f.set(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-400 pr-12 bg-white text-gray-800" />
                <button type="button" onClick={() => f.toggle(!f.show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {f.show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}
        </div>
        <button onClick={handleChangePassword} disabled={pwSaving}
          className="w-full mt-4 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white font-bold py-3.5 rounded-2xl flex items-center justify-center gap-2 transition">
          {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
          {pwSaving ? t("sup_dash_saving_pw") : t("sup_dash_save_pw")}
        </button>
      </div>
    </div>
  );
}

export default SupervisorDashboard;
